import logger from '../utils/logger';
import React, { createContext, useContext, useEffect, useState } from 'react';
import brandService from '../services/brandService';

const BrandContext = createContext();

export const useBrand = () => {
	const ctx = useContext(BrandContext);
	if (!ctx) throw new Error('useBrand must be used within a BrandProvider');
	return ctx;
};

const DEFAULT_BRAND = {
	name: 'CoffeeCloud',
	logoUrl: '/logo.png',
	faviconUrl: '/favicon.ico',
};

const updateFavicon = (faviconUrl) => {
	try {
		// Remove existing icons
		const links = document.querySelectorAll("link[rel*='icon']");
		links.forEach(l => l.parentNode?.removeChild(l));

		// Add ico
		const linkIco = document.createElement('link');
		linkIco.rel = 'icon';
		linkIco.type = 'image/x-icon';
		linkIco.href = `${faviconUrl}?t=${Date.now()}`;
		document.head.appendChild(linkIco);

		// Add png (optional better support)
		const linkPng = document.createElement('link');
		linkPng.rel = 'icon';
		linkPng.type = 'image/png';
		linkPng.href = `${faviconUrl}?t=${Date.now()}`;
		document.head.appendChild(linkPng);
	} catch (e) {
		// eslint-disable-next-line no-console
		logger.error('Failed to update favicon', e);
	}
};

export const BrandProvider = ({ children }) => {
	const [brand, setBrand] = useState(DEFAULT_BRAND);
	const [loading, setLoading] = useState(true);

	const refresh = async () => {
		try {
			setLoading(true);
			const fresh = await brandService.getBrandSettings(true);
			setBrand({
				name: fresh.name || DEFAULT_BRAND.name,
				logoUrl: fresh.logoUrl || DEFAULT_BRAND.logoUrl,
				faviconUrl: fresh.faviconUrl || DEFAULT_BRAND.faviconUrl,
			});
		} catch (e) {
			setBrand(DEFAULT_BRAND);
		} finally {
			setLoading(false);
		}
	};

	// Initial load
	useEffect(() => {
		refresh();
	}, []);

	// Cross-tab updates
	useEffect(() => {
		const onStorage = (ev) => {
			if (ev.key === 'brandSettingsUpdated') {
				refresh();
			}
		};
		const onFocus = () => refresh();
		window.addEventListener('storage', onStorage);
		window.addEventListener('focus', onFocus);
		return () => {
			window.removeEventListener('storage', onStorage);
			window.removeEventListener('focus', onFocus);
		};
	}, []);

	// Periodic refresh (every 30s)
	useEffect(() => {
		const id = setInterval(() => refresh(), 30000);
		return () => clearInterval(id);
	}, []);

	// Update favicon when brand changes
	useEffect(() => {
		if (brand?.faviconUrl) updateFavicon(brand.faviconUrl);
	}, [brand?.faviconUrl]);

	const updateBrand = async (next) => {
		const result = await brandService.updateBrandSettings(next);
		if (result?.success) {
			setBrand({
				name: next.name || DEFAULT_BRAND.name,
				logoUrl: next.logoUrl || DEFAULT_BRAND.logoUrl,
				faviconUrl: next.faviconUrl || DEFAULT_BRAND.faviconUrl,
			});
			// Trigger a fresh pull to ensure server is source of truth
			setTimeout(() => refresh(), 300);
		}
		return result;
	};

	const value = { brand, loading, refreshBrandSettings: refresh, updateBrandSettings: updateBrand };
	return (
		<BrandContext.Provider value={value}>
			{children}
		</BrandContext.Provider>
	);
};

export default BrandContext;
